/**
 * Platform layer — the single source of visual truth for Creativitas (§6).
 *
 * Creativitas is a platform that will carry several games, spreads and courses.
 * These tokens describe the *platform*: its shell, navigation, forms, tables and
 * dialogs. They stay the same whichever product a person is looking at.
 *
 * A product's own look — board art, card backs, table textures, illustrations —
 * lives in `product.ts` and is only ever applied inside a product surface. A
 * product may not restyle a button, a table or a dialog.
 *
 * Restyling the whole platform means editing this file, not the components.
 */
import type { MantineColorsTuple } from '@mantine/core';

/**
 * Deep teal — the platform's primary. Used for actions, focus and emphasis.
 * The dark end carries the brand; the light end is for tints, never for text.
 */
export const teal: MantineColorsTuple = [
  '#edf5f6',
  '#d9ebec',
  '#b5d8da',
  '#8fc3c8',
  '#66adb3',
  '#36949b',
  // The brand teal sits at index 6, which is the shade filled controls use.
  '#007781',
  '#00656e',
  '#00565d',
  '#00474d',
];

/**
 * Muted gold — a secondary accent for ownership, subscription and highlights.
 * Deliberately desaturated: gold is never a primary UI colour.
 */
export const gold: MantineColorsTuple = [
  '#fbf8f5',
  '#f6f0e9',
  '#eee2d4',
  '#e5d3be',
  '#dcc3a6',
  '#d1b08a',
  '#c59b6b',
  '#a7845b',
  '#8e704d',
  '#765d40',
];

/** Warm neutral for surfaces and quiet states. */
export const sand: MantineColorsTuple = [
  '#f9f7f2',
  '#efece4',
  '#ded9cc',
  '#cbc4b2',
  '#bab29d',
  '#aea690',
  '#a89f87',
  '#938b74',
  '#837b67',
  '#726a57',
];

export const RAW_COLORS = {
  /** Ivory — the platform ground. Warm, quiet, never textured. */
  ivory: '#fbf9f4',
  card: '#ffffff',
  elevated: '#f7f4ed',
  sunken: '#f2eee4',

  ink: '#14312f',
  inkMuted: '#5a6e6c',
  inkSubtle: '#8a9c9a',

  /* Snapped to the printed cards: brighter and bluer than a darkened teal. */
  teal: '#007781',
  tealHover: '#00656e',
  tealSoft: '#e6f1f2',

  gold: '#c59b6b',
  /* Gold never passes as text at brand strength — this is the readable one. */
  goldDeep: '#836726',
  goldSoft: '#f7f1ea',

  /** Borders stay faint: structure comes from spacing, not from lines. */
  border: 'rgba(20, 49, 47, 0.10)',
  borderStrong: 'rgba(20, 49, 47, 0.20)',

  danger: '#a8443a',
  dangerSoft: '#f8eae8',
  success: '#3a7d5c',
  successSoft: '#e7f1eb',
  warning: '#9a7434',
  warningSoft: '#f6efdf',

  lockedOverlay: 'rgba(20, 49, 47, 0.52)',
} as const;

/**
 * Semantic variables (§6). Components reference these names, never raw hex.
 *
 * Two font tokens, and the split between them is meaning, not size:
 * `--font-display` says what a page *is*; `--font-ui` runs the interface.
 */
export const SEMANTIC_VARS: Record<string, string> = {
  '--surface-page': RAW_COLORS.ivory,
  '--surface-card': RAW_COLORS.card,
  '--surface-elevated': RAW_COLORS.elevated,
  '--surface-sunken': RAW_COLORS.sunken,
  '--surface-accent-soft': RAW_COLORS.tealSoft,
  '--surface-gold-soft': RAW_COLORS.goldSoft,

  '--text-primary': RAW_COLORS.ink,
  '--text-muted': RAW_COLORS.inkMuted,
  '--text-subtle': RAW_COLORS.inkSubtle,
  '--text-on-accent': '#ffffff',

  '--border-subtle': RAW_COLORS.border,
  '--border-strong': RAW_COLORS.borderStrong,

  '--accent': RAW_COLORS.teal,
  '--accent-hover': RAW_COLORS.tealHover,
  '--accent-contrast': '#ffffff',
  '--gold': RAW_COLORS.gold,
  '--gold-deep': RAW_COLORS.goldDeep,

  '--danger': RAW_COLORS.danger,
  '--danger-soft': RAW_COLORS.dangerSoft,
  '--success': RAW_COLORS.success,
  '--success-soft': RAW_COLORS.successSoft,
  '--warning': RAW_COLORS.warning,
  '--warning-soft': RAW_COLORS.warningSoft,

  '--locked-overlay': RAW_COLORS.lockedOverlay,

  '--radius-control': '10px',
  '--radius-card': '14px',
  '--radius-surface': '18px',
  '--radius-pill': '999px',

  '--control-height-sm': '32px',
  '--control-height-md': '40px',
  '--control-height-lg': '48px',

  /**
   * Shadows are almost absent. Depth comes from a faint border and from the
   * ivory ground, not from a drop shadow under every card.
   */
  '--shadow-card': 'none',
  '--shadow-card-hover': '0 1px 2px rgba(20, 49, 47, 0.05)',
  '--shadow-elevated': '0 8px 28px -22px rgba(20, 49, 47, 0.35)',

  '--focus-ring': `0 0 0 3px ${RAW_COLORS.tealSoft}, 0 0 0 4px ${RAW_COLORS.teal}`,

  /** Cormorant — meaning, narrative, page identity. Variable 300–700 + italic. */
  '--font-display': "'Cormorant Variable', 'Cormorant', 'Georgia', 'Times New Roman', serif",
  /** Montserrat — navigation, controls, data, labels, system messages. */
  '--font-ui':
    "'Montserrat Variable', 'Montserrat', 'Segoe UI', system-ui, -apple-system, sans-serif",
  /*
    Моноширинный — системный, и это выбор, а не оплошность.

    Раньше список начинался с JetBrains Mono, которого платформа не
    доставляет: на машине разработчика он подменялся Cascadia Mono, у Дарьи на
    macOS — тем, что найдётся, вплоть до Courier. Начертание, которое зависит
    от того, что установлено у смотрящего, — это не выбранный шрифт.

    Тянуть ради двух мест (код ошибки и подсказка на входе) целое семейство
    незачем. `ui-monospace` даёт системный моноширинный, который на каждой
    платформе выглядит как свой: SF Mono на Apple, Cascadia на Windows.
  */
  '--font-mono':
    "ui-monospace, 'SF Mono', 'Cascadia Mono', 'Segoe UI Mono', Menlo, Consolas, monospace",

  /** Card artwork is 7:12 (70×120 mm trimmed) — see ИНВЕНТАРИЗАЦИЯ_МАТЕРИАЛОВ §4. */
  '--card-aspect': '7 / 12',
};

/**
 * Width of a live working surface — the game board, the spread table.
 *
 * Reading pages keep the comfortable measure of `Container size="xl"` (1320px):
 * a paragraph gets unreadable past it. A session is not read, it is watched —
 * the board, the turn control and every player's counters have to be on screen
 * at once — so it is allowed to take more of a large monitor.
 *
 * The number is the sum of what the surface actually holds, not a round guess:
 * a square board as tall as a screen comfortably allows (~1000px) beside a rail
 * wide enough for a player's whole counter row (~480px). Past that there is
 * nothing left to stretch, and the extra width would only pull the board and
 * the players apart.
 */
export const SESSION_SURFACE_WIDTH = 1520;

/** Spacing scale in px. Generous: whitespace is the main structural device. */
export const SPACING = {
  xs: 8,
  sm: 12,
  md: 20,
  lg: 32,
  xl: 52,
} as const;

export const RADIUS = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 14,
  xl: 18,
} as const;

/** Image tiers produced by tools/build_web_assets.py. */
export const IMAGE_TIERS = {
  cardThumb: 'w210',
  cardPlay: 'w420',
  cardFull: 'w840',
} as const;
